/**
 * TransferReportsSearchStep View Component
 *
 * Renders the search input and player results list for selecting
 * a target provider player. Uses FlashList for performant scrolling
 * with an amber-accented utilitarian design. Includes a clear button
 * that appears when the input has text.
 * @module features/player-detail/components/TransferReportsSearchStep
 */

import { FlashList, type ListRenderItem } from "@/components/atoms/FlashList";
import { Search, X } from "lucide-react-native";
import { memo, type ReactElement } from "react";

import { Box } from "@/components/atoms/Box";
import { Input } from "@/components/atoms/Input";
import { Stack } from "@/components/atoms/Stack";
import { ifWeb } from "@/utils/platform";
import type { PlayerSearchPlayerFragment } from "@/generated/graphql";
import defaultExtractKey from "@/utils/defaultExtractKey";

/** Props for TransferReportsSearchStepView */
export interface TransferReportsSearchStepViewProps {
  /** Current search input value */
  readonly searchValue: string;
  /** Filtered list of provider-sourced players */
  readonly players: readonly PlayerSearchPlayerFragment[];
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Callback fired when search text changes */
  readonly onSearchChange: (text: string) => void;
  /** Callback fired when clear button is pressed */
  readonly onClear: () => void;
  /** Callback fired when list reaches end for pagination */
  readonly onLoadMore: () => void;
  /** Render function for each player result row */
  readonly renderItem: ListRenderItem<PlayerSearchPlayerFragment>;
  /** Render function for the empty state */
  readonly renderEmpty: () => ReactElement;
}

/**
 * Presentational component for the target player search step
 * @param root0 - Component props
 * @param root0.searchValue - Current search input value
 * @param root0.players - Filtered provider-sourced players
 * @param root0.onSearchChange - Search input change handler
 * @param root0.onClear - Clear button handler
 * @param root0.onLoadMore - Pagination load-more handler
 * @param root0.renderItem - Render function for player rows
 * @param root0.renderEmpty - Render function for empty state
 */
const TransferReportsSearchStepView = ({
  searchValue,
  players,
  onSearchChange,
  onClear,
  onLoadMore,
  renderItem,
  renderEmpty,
}: TransferReportsSearchStepViewProps) => (
  <Stack className="flex-1">
    <Box
      className={`border-border-outline-subtle border-b ${ifWeb("px-4 py-3", "py-3")}`}
    >
      <Input
        variant="outline"
        size="md"
        leadingIcon={Search}
        trailingIcon={searchValue.length > 0 ? X : undefined}
        onTrailingIconPress={onClear}
        field={{
          placeholder: "Search by player name...",
          value: searchValue,
          onChangeText: onSearchChange,
          testID: "transfer-search-input",
        }}
      />
    </Box>

    <FlashList
      data={players as PlayerSearchPlayerFragment[]}
      renderItem={renderItem}
      keyExtractor={defaultExtractKey}
      onEndReached={onLoadMore}
      onEndReachedThreshold={0.5}
      ListEmptyComponent={renderEmpty}
    />
  </Stack>
);

TransferReportsSearchStepView.displayName = "TransferReportsSearchStepView";

export default memo(TransferReportsSearchStepView);
